import { getPaymentIssueMessage } from "@/utils/subscriptionStatus";

export const planHierarchy = ["free", "pro", "team", "enterprise"];

export interface PlanCtaDisabledReasonParams {
  isButtonDisabled: boolean;
  isLoading: boolean;
  isCurrentPlan: boolean;
  isTrialing: boolean;
  isTargetFreePlan: boolean;
  isMaxUsersReached: boolean;
  totalCurrentMembers: number;
  maxUsersForPlan: number;
  targetPlanLabel: string;
  isPaymentBlocked: boolean;
  stripeSubscriptionStatus: string | null | undefined;
  isBannerPermissionDisabled: boolean;
}

export const getPlanCtaDisabledReason = (
  params: PlanCtaDisabledReasonParams,
): string | undefined => {
  if (!params.isButtonDisabled) {
    return undefined;
  }
  if (params.isLoading) {
    return "Please wait while we update your subscription.";
  }
  if (params.isCurrentPlan && !params.isTrialing) {
    return "This is your current plan.";
  }
  if (params.isTrialing && params.isTargetFreePlan) {
    return "You can't switch to the Free plan during your trial. Wait for the trial to end or contact support if you need to cancel.";
  }
  if (params.isMaxUsersReached) {
    const limitLabel =
      params.maxUsersForPlan >= 999999 ? "unlimited" : String(params.maxUsersForPlan);
    const seatWord = params.maxUsersForPlan === 1 ? "seat" : "seats";
    return `The ${params.targetPlanLabel} plan allows up to ${limitLabel} ${seatWord}. Your organization has ${params.totalCurrentMembers} members. Remove members before switching.`;
  }
  if (params.isPaymentBlocked) {
    return getPaymentIssueMessage(params.stripeSubscriptionStatus ?? "");
  }
  if (params.isBannerPermissionDisabled) {
    return "Only organization owners can change the plan.";
  }
  return "This option is unavailable.";
};

export const geIsDowngrade = (userPlan: string, targetPlan: string) => {
  const userPlanIndex = planHierarchy?.indexOf(userPlan?.toLowerCase());
  const targetPlanIndex = planHierarchy?.indexOf(targetPlan?.toLowerCase());
  return userPlanIndex > targetPlanIndex;
};

export const getButtonText = (
  planName: string,
  userPlan: string,
  status: string,
  hasLegacySubscription: boolean,
) => {
  const normalizedPlanName = planName.toLowerCase();
  const normalizedUserPlan = userPlan?.toLowerCase();
  const isTrial = status === "trialing";

  if (hasLegacySubscription) {
    if (normalizedPlanName === "enterprise") {
      return "Contact Sales";
    }
    if (normalizedPlanName === "free") {
      return "Downgrade to Free";
    }
    return `Switch to ${planName}`;
  }

  if (isTrial && normalizedPlanName === "free") {
    return "You are on a free trial";
  }

  if (normalizedPlanName === "free" && normalizedUserPlan === "free") {
    return "You are on the Free plan";
  }
  if (normalizedPlanName === "team" && normalizedUserPlan === "team") {
    if (isTrial) {
      return "Upgrade";
    }
    return "You are on the Team plan";
  }
  if (normalizedPlanName === "pro" && normalizedUserPlan === "pro") {
    return "You are on the Pro plan";
  }
  if (normalizedPlanName === "enterprise" && normalizedUserPlan === "enterprise") {
    return "You are on the Enterprise plan";
  }

  if (normalizedPlanName === "enterprise") {
    return "Contact Sales";
  }

  const isDowngrade = geIsDowngrade(normalizedUserPlan, normalizedPlanName);

  if (isDowngrade && !isTrial) {
    return "Downgrade";
  }

  if (isTrial && normalizedPlanName !== "free") {
    return "Upgrade";
  }

  if (!isDowngrade && normalizedPlanName !== "enterprise") {
    return "Upgrade";
  }

  return "You are on this plan";
};
